import React, {useEffect,useRef,useState} from 'react';
import {APP_ID,BPA_SESSION_STORAGE_KEY,authenticate,restoreSession,clearSession,requestPasswordReset,getStoredSession,confirmPasswordReset} from '../lib/bpaAuth.js';

import {clearGoogleAuth,setGoogleScope} from '../lib/expenseGoogleSheets.js';
import {dbService} from '../services/dbService.js';
import * as Fi from 'react-icons/fi';
import {buildLoginSupportMailto} from '../lib/loginSupport.js';
import {ExpenseShowcaseModal} from './ExpenseShowcase.jsx';

// The financial application is NEVER mounted until PocketBase verifies the
// customer and this exact app entitlement. A separate IndexedDB is then used.

// Consolidated from src/components/CustomerPasswordSetup.jsx; keep its behavior intact.
const CustomerPasswordSetup = (()=>{


// This is the customer-owned application's one-time PocketBase password form.
// The reset token is supplied by the server email inside a URL fragment, not
// uploaded to Netlify or shared with another service.
function CustomerPasswordSetup({token,onBack}) {
  const [password,setPassword]=useState('');
  const [confirmation,setConfirmation]=useState('');
  const [busy,setBusy]=useState(false);
  const [complete,setComplete]=useState(false);
  const [error,setError]=useState('');

  async function submit(event) {
    event.preventDefault();
    if(busy)return;
    setError('');
    if(password.length<12)return setError('Choose a password of at least 12 characters.');
    if(password!==confirmation)return setError('Passwords do not match.');
    setBusy(true);
    try{
      await confirmPasswordReset(token,password,confirmation);
      setPassword('');
      setConfirmation('');
      setComplete(true);
    }catch(err){
      setError('Unable to save your password. Your link may be expired or already used. Request a new one from the Expense Manager sign-in screen.');
    }finally{setBusy(false);}
  }
  return <main className="min-h-[100dvh] flex items-center justify-center bg-[#f3f6f9] px-4 py-8 font-sans">
    <section aria-label="Expense Manager password setup" className="w-full max-w-md rounded-3xl bg-white shadow-xl shadow-slate-200/60 border border-slate-200 p-6 sm:p-9">
      <div className="inline-flex rounded-2xl bg-[#0176D3] text-white p-3 mb-6" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><path d="M4 16V9l5-4 4 4 3-6 4 3v10H4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M4 20h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
      </div>
      <p className="text-[#0176D3] text-xs tracking-widest uppercase font-bold mb-2">BPA SOLUTIONS</p>
      <h1 className="text-2xl font-bold text-[#032D60] mb-3">{complete?'Password saved':'Set your password'}</h1>
      {complete?
        <div className="space-y-4">
          <p role="status" className="rounded-xl bg-green-50 text-green-800 p-4 text-sm">Your BPA customer password is now set. You can sign in to Expense Manager Pro.</p>
          <p className="text-sm text-slate-600">This is your shared BPA customer password for any other BPA apps you can access.</p>
          <button type="button" onClick={onBack} className="w-full rounded-xl bg-[#0176D3] text-white font-semibold py-3">Continue to Expense Manager sign in</button>
        </div>:
        <>
          <p className="text-slate-600 text-sm leading-relaxed mb-6">Create a secure password for your Expense Manager Pro account. It also protects other BPA applications linked to this same customer login.</p>
          <form onSubmit={submit} className="space-y-4">
            <label className="block text-sm font-medium text-slate-700">New password
              <input aria-label="New BPA password" autoComplete="new-password" type="password" required minLength={12} value={password} onChange={e=>setPassword(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-blue-600"/>
            </label>
            <label className="block text-sm font-medium text-slate-700">Confirm new password
              <input aria-label="Confirm BPA password" autoComplete="new-password" type="password" required minLength={12} value={confirmation} onChange={e=>setConfirmation(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-blue-600"/>
            </label>
            {error&&<p role="alert" className="rounded-xl bg-red-50 text-red-700 p-3 text-sm">{error}</p>}
            <button disabled={busy} type="submit" className="w-full rounded-xl bg-[#0176D3] text-white font-semibold py-3 disabled:opacity-60">{busy?'Saving securely…':'Set password'}</button>
          </form>
          <button type="button" className="text-[#0176D3] text-sm font-medium underline underline-offset-4 mt-4" onClick={onBack}>Return to sign in</button>
        </>
      }
      <p className="text-center text-slate-400 text-xs mt-7">Powered by BPA Solutions</p>
    </section>
  </main>;
}

return CustomerPasswordSetup;
})();

function readPasswordSetupToken(){
  const prefix='#/set-password?';
  if(!window.location.hash.startsWith(prefix))return '';
  const token=new URLSearchParams(window.location.hash.slice(prefix.length)).get('token')||'';
  return token.length<=4096?token:'';
}

export default function CustomerAccessGate({children}) {
  // Do not remove the URL fragment during render: React StrictMode may
  // invoke initializers twice. Strip it in the effect after capture.
  const [resetToken]=useState(readPasswordSetupToken);
  const [resetScreen,setResetScreen]=useState(Boolean(resetToken));
  const [phase,setPhase]=useState(APP_ID?'checking':'unconfigured');
  const [session,setSession]=useState(null);
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [showPassword,setShowPassword]=useState(false);
  const [showPreview,setShowPreview]=useState(false);
  const [resetBusy,setResetBusy]=useState(false);
  const [supportIssue,setSupportIssue]=useState('Unable to sign in');
  const [error,setError]=useState('');
  const [notice,setNotice]=useState('');
  const generation=useRef(0);
  const currentId=useRef('');
  function lock(message='') {
    generation.current+=1;
    currentId.current='';
    clearGoogleAuth();
    dbService.releaseWorkspace();
    setSession(null);
    setPhase(APP_ID?'login':'unconfigured');
    setError(message);
  }
  function adopt(value) {
    if(!value?.token || !/^[a-z0-9]{15}$/.test(String(value.record?.id||'')) ||
       !/^[a-z0-9]{15}$/.test(String(value.access?.entitlementId||''))) {
      throw new Error('An active BPA account and Expense Manager entitlement are required.');
    }
    if(value.access?.customer?.id && value.access.customer.id!==value.record.id) {
      throw new Error('This purchased application does not belong to the signed-in customer.');
    }
    dbService.useCustomerWorkspace(value.record.id);
    setGoogleScope(value.record.id);
    currentId.current=value.record.id;
    setSession(value);
    setPhase('ready');
    setError('');
  }
  async function restore() {
    const run=++generation.current;
    try {
      const value=await restoreSession();
      if(run!==generation.current)return;
      if(value) adopt(value);
      else lock();
    } catch(e) {
      if(run!==generation.current)return;
      clearSession();
      lock(e.message);
    }
  }
  useEffect(()=>{
    // Lock legacy IndexedDB before asynchronous customer verification begins.
    dbService.releaseWorkspace();
    if(resetToken){
      clearSession();
      clearGoogleAuth();
      // Fragment URLs are not sent to Netlify; remove the sensitive token
      // from the address bar and browser history as soon as it is captured.
      window.history.replaceState({},'',window.location.pathname+window.location.search);
      setPhase('login');
    }else if(APP_ID)restore();
    const onChange=()=>{
      if(resetToken)return;
      if(!getStoredSession()?.token){lock();return;}
      const other=getStoredSession()?.record?.id;
      // Ignore token refreshes for the same user to avoid cross-tab loops.
      if(other && currentId.current===other)return;
      // A different account must never see the previous customer's dashboard
      // while its entitlement refresh is still pending.
      currentId.current='';
      dbService.releaseWorkspace();
      clearGoogleAuth();
      setSession(null);
      setPhase('checking');
      restore();
    };
    const onStorage=event=>{
      if(event.key===BPA_SESSION_STORAGE_KEY)onChange();
    };
    window.addEventListener('storage',onStorage);
    window.addEventListener('bpa:auth-changed',onChange);
    return ()=>{generation.current+=1;window.removeEventListener('storage',onStorage);
      window.removeEventListener('bpa:auth-changed',onChange);};
  },[]);
  async function login(event) {
    event.preventDefault();
    setError('');setNotice('');setSupportIssue('Unable to sign in');setPhase('checking');
    const run=++generation.current;
    try {
      const value=await authenticate(email,password);
      if(run!==generation.current)return;
      adopt(value);setPassword('');
    }catch(e){if(run===generation.current)lock(e.message)}
  }
  async function resetPassword(){
    setError('');setNotice('');setSupportIssue('Password reset not working');
    if(!email.trim()){setError('Enter your registered email address above, or contact BPA Support if you cannot remember it.');return;}
    setResetBusy(true);
    try{await requestPasswordReset(email);setNotice('If this address is registered, a password-reset email has been requested. If it does not arrive, contact BPA Support below.')}
    catch(e){setError(e.message)}
    finally{setResetBusy(false)}
  }
  const supportHref=buildLoginSupportMailto({
    email,issue:supportIssue,error,
    origin:typeof window!=='undefined'?window.location.origin+window.location.pathname:'',
    userAgent:typeof navigator!=='undefined'?navigator.userAgent:''
  });
  if(resetScreen&&resetToken)return <CustomerPasswordSetup token={resetToken}
    onBack={()=>{setResetScreen(false);lock();setNotice('Sign in using your BPA customer email and password.');}}/>;
  if(phase==='ready' && session) return <React.Fragment key={session.record.id}>{children}</React.Fragment>;
  return <main className="relative isolate min-h-[100dvh] flex items-center justify-center overflow-hidden bg-[#f0f5fc] px-4 py-6 sm:px-6 sm:py-10 font-sans">
    <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true" style={{backgroundImage:'radial-gradient(#b8cde9 0.85px,transparent 0.85px)',backgroundSize:'22px 22px',opacity:0.4}}/>
    <div className="absolute -top-48 -right-40 w-[580px] h-[580px] rounded-full bg-blue-200/40 blur-3xl -z-10" aria-hidden="true"/>
    <div className="absolute -bottom-48 -left-32 w-[520px] h-[520px] rounded-full bg-sky-100 blur-3xl -z-10" aria-hidden="true"/>
    <div className="w-full max-w-[1050px] min-h-[650px] grid lg:grid-cols-[1.02fr_0.98fr] overflow-hidden rounded-[30px] border border-[#dce7f5] bg-white shadow-[0_24px_100px_rgba(3,45,96,0.13)]">
      <aside className="relative hidden lg:flex flex-col justify-between gap-12 overflow-hidden bg-gradient-to-br from-[#032D60] via-[#06447f] to-[#0176D3] p-10 xl:p-12 text-white">
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="bpaLoginDots" width="40" height="40" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.2" fill="#b8e0ff"/></pattern><linearGradient id="bpaLoginSheen" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#ffffff" stopOpacity=".16"/><stop offset="1" stopColor="#ffffff" stopOpacity="0"/></linearGradient></defs>
          <rect width="100%" height="100%" fill="url(#bpaLoginDots)"/><circle cx="480" cy="45" r="220" fill="none" stroke="#97ccff" strokeWidth="1"/><circle cx="480" cy="45" r="310" fill="none" stroke="#97ccff" strokeWidth="1"/><circle cx="-45" cy="680" r="240" fill="url(#bpaLoginSheen)"/>
        </svg>
        <div className="relative flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-[14px] border border-white/20 bg-white/15 backdrop-blur-sm"><Fi.FiLayers size={24}/></span>
          <span className="text-xs font-bold tracking-[0.22em]">BPA SOLUTIONS <span className="block font-normal text-blue-100 tracking-widest mt-1">FINANCIAL WORKSPACE</span></span>
        </div>
        <div className="relative space-y-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-sky-100"><Fi.FiShield/> Secure customer workspace</span>
          <h2 className="text-[43px] leading-[1.12] tracking-[-0.035em] font-extrabold max-w-sm">A clearer view of your money.</h2>
          <p className="text-[15px] leading-7 text-blue-100 max-w-sm">One secure space to organise transactions, stay on top of budgets and keep your financial records within reach.</p>
          <div className="relative rounded-[22px] border border-white/25 bg-white/[0.12] backdrop-blur-lg shadow-xl p-5 space-y-5 max-w-[390px]" aria-hidden="true">
            <div className="flex items-center justify-between"><div className="space-y-1"><div className="text-xs text-blue-100">Monthly overview</div><div className="font-bold text-lg">Your finances, simplified</div></div><span className="p-2 rounded-lg bg-white/15"><Fi.FiBarChart2 size={19}/></span></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/10 px-3 py-3"><span className="text-xs text-sky-100">Stay organised</span><div className="mt-2 flex items-center gap-2 text-sm font-semibold"><Fi.FiCheckCircle/> Transactions</div></div>
              <div className="rounded-xl border border-white/10 bg-white/10 px-3 py-3"><span className="text-xs text-sky-100">Plan confidently</span><div className="mt-2 flex items-center gap-2 text-sm font-semibold"><Fi.FiTarget/> Budgets</div></div>
            </div>
            <svg className="h-20 w-full" viewBox="0 0 350 85" fill="none" preserveAspectRatio="none"><path d="M0 67 C28 62 45 45 70 51 S112 64 137 40 S180 47 202 23 S247 44 269 20 S315 30 350 6" stroke="#8EE9C7" strokeWidth="3" strokeLinecap="round"/><path d="M0 67 C28 62 45 45 70 51 S112 64 137 40 S180 47 202 23 S247 44 269 20 S315 30 350 6 L350 85 L0 85Z" fill="#8EE9C7" opacity=".12"/></svg>
          </div>
        </div>
        <div className="relative flex items-center gap-2 text-xs font-medium text-blue-100"><Fi.FiLock/> Access is checked against your BPA application licence.</div>
      </aside>
      <section className="min-w-0 flex flex-col justify-center px-6 py-8 sm:px-11 sm:py-12 lg:px-10 xl:px-14" aria-label="Expense Manager customer access">
        <div className="flex items-center gap-3 lg:hidden mb-7"><span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0176D3] text-white"><Fi.FiLayers size={23}/></span><span className="text-[#032D60] text-xs font-extrabold tracking-[0.15em]">BPA SOLUTIONS</span></div>
        <div className="mb-7">
          <p className="text-[#0176D3] text-xs tracking-[0.18em] uppercase font-bold mb-2.5">Your secure financial workspace</p>
          <h1 className="text-[29px] sm:text-[33px] leading-tight tracking-[-0.03em] font-extrabold text-[#032D60]">Welcome back</h1>
          <p className="font-bold text-[#113c72] mt-2 text-base">Expense Manager Pro <span className="text-[#8095b0] font-medium text-xs">2.0</span></p>
          <p className="text-slate-600 text-sm leading-6 mt-2">Sign in to securely access your personal financial workspace.</p>
        </div>
        {phase==='unconfigured'&&<div role="alert" className="rounded-xl bg-amber-50 text-amber-900 p-4 text-sm mb-4">This application needs its permanent BPA application ID configured by BPA Solutions before customers can sign in.</div>}
        {phase==='checking'&&<div role="status" className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900 flex items-center gap-2 mb-4"><Fi.FiLoader className="animate-spin"/> Checking your BPA account and access...</div>}
        {phase==='login'&&<form onSubmit={login} className="space-y-4">
          <label className="block text-sm font-semibold text-[#25476d]">Email address
            <span className="relative block mt-2">
              <Fi.FiMail aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input autoComplete="email" type="email" aria-label="BPA customer email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" className="w-full rounded-xl border border-slate-200 bg-[#f7faff] pl-11 pr-4 py-3.5 text-base font-normal text-[#032D60] shadow-sm focus:border-[#0176D3] focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-colors"/>
            </span>
          </label>
          <label className="block text-sm font-semibold text-[#25476d]">Password
            <span className="relative block mt-2">
              <Fi.FiLock aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input autoComplete="current-password" type={showPassword?'text':'password'} aria-label="BPA password" required value={password} onChange={e=>setPassword(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-[#f7faff] pl-11 pr-14 py-3.5 text-base font-normal text-[#032D60] shadow-sm focus:border-[#0176D3] focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-colors"/>
              <button type="button" aria-label={showPassword?'Hide password':'Show password'} aria-pressed={showPassword} onClick={()=>setShowPassword(x=>!x)} title={showPassword?'Hide password':'Show password'} className="absolute right-1 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-lg text-slate-500 hover:bg-blue-50 hover:text-[#0176D3] focus-visible:outline-2"><span aria-hidden="true">{showPassword?<Fi.FiEyeOff size={19}/>:<Fi.FiEye size={19}/>}</span></button>
            </span>
          </label>
          {error&&<p role="alert" className="rounded-xl border border-red-100 bg-red-50 text-red-800 p-3 text-sm">{error}</p>}
          {notice&&<p role="status" className="rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-900 p-3 text-sm">{notice}</p>}
          <div className="flex justify-end">
            <button type="button" disabled={resetBusy} onClick={resetPassword} className="text-[#0176D3] text-sm font-semibold hover:underline underline-offset-4 disabled:opacity-60 disabled:cursor-wait inline-flex items-center gap-2">{resetBusy&&<Fi.FiLoader className="animate-spin"/>}{resetBusy?'Requesting reset…':'Forgot password?'}</button>
          </div>
          <button type="submit" className="w-full rounded-xl bg-[#0176D3] text-white font-bold py-3.5 hover:bg-[#0064b8] hover:shadow-lg active:translate-y-px transition-all inline-flex items-center justify-center gap-2 cursor-pointer">Sign in securely <Fi.FiArrowRight/></button>
        </form>}
        {phase==='login'&&<div className="mt-6 border-t border-[#e2eaf4] pt-5">
          <div className="flex items-center gap-3 text-xs font-semibold text-[#7a8ca5]"><span className="h-px flex-1 bg-[#e2eaf4]"/>or explore first<span className="h-px flex-1 bg-[#e2eaf4]"/></div>
          <button type="button" onClick={()=>setShowPreview(true)}
            className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-[#0176D3] bg-white px-4 py-3 text-sm font-bold text-[#0359ac] shadow-sm transition hover:bg-[#edf7ff] focus-visible:outline-2 focus-visible:outline-[#0176D3]">
            <Fi.FiEye size={19} aria-hidden="true"/> See Inside the App
          </button>
          <p className="mt-2 text-center text-xs leading-5 text-[#617895]">Explore five real application screens without signing in.</p>
          <p className="mt-1 text-center text-xs">
            <a href="/preview" target="_blank" rel="noopener noreferrer"
              className="font-semibold text-[#0176D3] underline underline-offset-2 hover:text-[#034f92]">Open the shareable preview page</a>
          </p>
        </div>}
        {phase!=='login'&&error&&<p role="alert" className="rounded-xl bg-red-50 text-red-800 p-3 mt-3 text-sm">{error}</p>}
        <div className="mt-7 border-t border-slate-200 pt-5">
          <p className="text-sm font-semibold text-[#25476d]">Having trouble accessing your account?</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">Whether you cannot sign in or your reset email is missing, our support team can help.</p>
          <a href={supportHref} className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#c7dbf1] bg-[#f3f8ff] px-4 py-3 text-sm font-bold text-[#0165b5] hover:border-[#0176D3] hover:bg-[#eaf4ff] focus-visible:outline-2 cursor-pointer"><Fi.FiLifeBuoy/> Email BPA Support <Fi.FiArrowUpRight size={14}/></a>
          <p className="mt-2 text-xs text-slate-500">Opens a prepared email with your entered username, app ID, website and diagnostic details. Your password is never included.</p>
        </div>
        <div className="mt-8 flex items-center justify-between gap-2 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1.5"><Fi.FiShield className="text-emerald-600"/> Licence-verified access</span>
          <span>Powered by <strong className="font-semibold text-[#66809f]">BPA Solutions</strong></span>
        </div>
      </section>
    </div>
    {showPreview&&phase==='login'&&<ExpenseShowcaseModal onClose={()=>setShowPreview(false)}/>}
  </main>;
}